import { IResponse, PureRestRequest } from '@portals/fetch-api';
import { requireModule } from '@wildberries/service-modules-load';

const cachedScripts = new Map();

export const loadScript = (
  name: string,
  staticURL: string,
  jsUrl: string,
  fromCDN?: boolean,
  cdnData: {
    APP_STATIC_NAMESPACE?: string;
    WB_STATIC_URL?: string;
    APP_VERSION?: string;
  } = {},
  appVersion?: string,
  isLocalhost?: boolean,
  externalPackages?: Record<string, any>,
) => {
  const { APP_STATIC_NAMESPACE, WB_STATIC_URL, APP_VERSION } = cdnData;

  // check if localhost - try dev prefix
  const cdnPrefix = isLocalhost ? 'dev' : appVersion;

  // get cdn project path from jsUrl
  const cdnUrl =
    fromCDN && appVersion
      ? `${WB_STATIC_URL}/${APP_STATIC_NAMESPACE}/${APP_VERSION}-${cdnPrefix}/`
      : jsUrl;

  // get endpoint for index.js file
  const endpoint = fromCDN ? jsUrl : `${staticURL}/${jsUrl}`;

  // get path for replacement in the code
  const replaceStreamPublicPath = fromCDN ? cdnUrl : `${staticURL}/umd/`;

  let promise;

  if (cachedScripts.has(endpoint)) {
    promise = cachedScripts.get(endpoint);
  } else {
    promise = new Promise(async (resolve, reject) => {
      try {
        const { data, error, errorText } =
          (await new PureRestRequest().getRequest({
            mode: 'cors',
            parseType: 'text',
            endpoint,
            retry: 2,
            extraValidationCallback: () => true,
            cache: fromCDN ? 'force-cache' : 'default',
          })) as IResponse & { data: string };

        if (error) {
          throw new Error(errorText);
        }

        // pathToReplaceRuntimeEnvs - publicPath for window env with "runtime-config.json"
        // replaceStreamPublicPath - publicPath for webpack react apps
        // publicPathForReplace - old publicPath for webpack react apps
        // AND for other tools that use relative path to service
        const source = data
          .replace(
            /pathToReplaceRuntimeEnvs/g,
            `/nsf/ui/${APP_STATIC_NAMESPACE}/umd/`,
          )
          .replace(/replaceStreamPublicPath/g, replaceStreamPublicPath)
          .replace(/publicPathForReplace/g, `${staticURL}/umd/`);

        // eslint-disable-next-line no-new-func
        const extensionFn = new Function(
          'exports',
          'require',
          'module',
          source,
        );

        const module = {
          exports: {},
        };

        extensionFn(
          module.exports,
          requireModule.bind(null, externalPackages || {}),
          module,
        );

        resolve(module.exports);
      } catch (error) {
        console.error('error in loadScript', error);

        reject(new Error(`"${name}" was not created by "${endpoint}"`));
      }
    });

    cachedScripts.set(endpoint, promise);
  }

  return promise
    .then((component: any) => {
      if (component) {
        return component;
      }

      throw new Error(`"${name}" was not created by "${endpoint}"`);
    })
    .catch(console.error);
};
